import { useState } from "react"
import { VIA_RU_NOTE } from "../../api/config"
import { describeError } from "../../api/http"
import { websiteWrites } from "../../api/mutations"
import { PartialRefreshTable, type Catalog, type WebsiteIdentity } from "../../api/types"
import { ActionNotes, Note } from "../../components/Note"
import { useAction } from "../../hooks/useAction"
import { duration, PARTIAL } from "./tables"

/**
 * Точечное обновление уже загруженного сайта: сверяет и правит, не очищая.
 *
 * Выключено по той же причине, что и массовая загрузка: `partial-refresh` есть
 * только в `ecm`, а тот ходит в базу сайта из России и на неудаче роняет
 * процесс. Ждёт рижского двойника (`PLAN.md`, фаза 7-Л).
 *
 * Сайт и каталог выбираются в шапке страницы (`Publish.tsx`), далеко от
 * кнопки — легко нажать «Обновить», не глядя наверх, что вообще выбрано.
 * Поэтому здесь отдельной строкой явно повторено, что именно заденет
 * операция: таблица, сайт и каталог — три метки рядом с кнопкой.
 */
export default function PartialPanel({
	site,
	catalog,
}: {
	site: WebsiteIdentity
	catalog: Catalog
}) {
	const action = useAction({ describe: describeError })
	const [table, setTable] = useState<PartialRefreshTable>(
		PartialRefreshTable.OcProductDescription
	)

	const current = PARTIAL.find(item => item.table === table)!

	async function refresh() {
		const started = performance.now()
		await action.run(
			() => websiteWrites.partialRefresh(site.id, catalog.id, table),
			`«${current.label}» обновлены за ${duration(
				Math.round(performance.now() - started)
			)}`
		)
	}

	return (
		<details className="panel pub-fold">
			<summary className="panel__head">
				<span>Точечное обновление сайта</span>
				<span className="panel__spacer" />
				<span className="muted">без очистки таблиц</span>
			</summary>

			<div className="panel__body">
				<p className="muted pub-lead">
					Обновляет уже загруженный сайт, сравнивая его с ERP. Дешевле массовой
					загрузки и не оставляет сайт с пустыми таблицами — этим догоняют
					переводы и правки между полными заливками.
				</p>

				<Note tone="warn">
					<b>Выключено.</b> {VIA_RU_NOTE}
				</Note>

				<div className="pub-actions">
					<select
						className="select"
						value={table}
						onChange={event => setTable(Number(event.target.value))}
					>
						{PARTIAL.map(item => (
							<option key={item.table} value={item.table}>
								{item.label}
							</option>
						))}
					</select>

					<button
						className="btn btn--primary"
						disabled
						title={VIA_RU_NOTE}
						onClick={() => void refresh()}
					>
						Обновить
					</button>

					{action.busy && <span className="spinner" />}
				</div>

				<div className="pub-actions">
					<span className="muted">Меняем:</span>
					<span className="tag tag--muted">{current.label}</span>
					<span className="muted">на сайте</span>
					<span className="tag tag--muted">{site.name}</span>
					<span className="muted">каталог</span>
					<span className="tag tag--muted">{catalog.name}</span>
				</div>
				<p className="muted">{current.note}</p>

				<ActionNotes action={action} />
			</div>
		</details>
	)
}
